import {Dispatch, useCallback, useEffect, useRef, useState} from 'react';

import {useSelector} from 'react-redux';
import {useApiIsLoaded} from '@vis.gl/react-google-maps';

import {GeolocationPlace, isLocationAccessPermitted} from '~/shared/services/geocoder';
import {selectCurrentAddressKey} from '~/shared/store/storeModules/currentAddressKey/currentAddressKeySelectors';
import {getLocalizationService} from '~/shared/services/localisationService';
import useGoogleMapsAutocomplete from '~/shared/components/CustomAddressAutoComplete/useGoogleMapsAutocomplete';
import {trackEvent} from '~/shared/services/analytics';
import {createLogger} from '~/shared/logging';
import {AddressFinderLocation} from '~/shared/services/analytics/analyticsModels';

import {CURRENT_LOCATION_PLACE_ID} from './consts';

const logger = createLogger('useAddressAutocomplete');

const useAddressAutocomplete = ({
  isMaxMobile,
  resetAddress,
  onCurrentLocationClick,
  onPlaceSelected,
  removeValidationError,
  setIsLoading,
  location,
}: {
  isMaxMobile?: boolean;
  resetAddress?: () => void;
  onPlaceSelected: (place: GeolocationPlace) => Promise<void>;
  onCurrentLocationClick?: ({setAddress}: {setAddress: (address: string) => void}) => Promise<void>;
  removeValidationError?: () => void;
  setIsLoading?: Dispatch<boolean>;
  location: AddressFinderLocation;
}) => {
  const {t} = getLocalizationService();
  const inputRef = useRef<HTMLInputElement>(null);
  const [address, setAddress] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [locationAccessEnabled, setLocationAccessEnabled] = useState(true);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const currentAddressKey = useSelector(selectCurrentAddressKey);
  const isGoogleMapReady = useApiIsLoaded();

  const {predictions, geocodeByPlaceId} = useGoogleMapsAutocomplete(address, 300);

  const currentLocationSuggestion = {
    distanceMeters: null,
    mainText: null,
    placeId: CURRENT_LOCATION_PLACE_ID,
    secondaryText: null,
    text: {
      matches: [],
      text: locationAccessEnabled ? t('use_my_current_location') : t('location_access_denied'),
    },
    types: [],
  } as unknown as google.maps.places.AutocompleteSuggestion['placePrediction'];

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value;
    if (newAddress.length === 2) {
      trackEvent('addressStartSearch', {
        searchOption: 'typing',
        addressBarType: location,
      });
    }
    resetAddress?.();
    setAddress(newAddress);
  };

  const onInputClick = () => {
    if (isMaxMobile) {
      setAddress('');
      resetAddress?.();
    }

    if (!isInputFocused) {
      setIsInputFocused(true);
      inputRef.current?.focus();
    }

    if (removeValidationError) {
      removeValidationError();
    }
  };

  const handleSelect = async (selectedAddressString: string, placeId: string) => {
    if (setIsLoading) {
      setIsLoading(true);
    }
    setIsInputFocused(false);
    setIsDropdownOpen(false);
    try {
      if (resetAddress) {
        resetAddress();
      }
      if (placeId === CURRENT_LOCATION_PLACE_ID) {
        await onCurrentLocationClick?.({setAddress});
        return;
      }

      setAddress(selectedAddressString);
      const results = await geocodeByPlaceId(placeId);
      if (!results) {
        return;
      }
      const result: GeolocationPlace = {...results[0], name: results[0].formatted_address};
      await onPlaceSelected(result);
    } catch (error) {
      logger.error('Error while selecting address', {error});
    } finally {
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  const clearInput = useCallback(() => {
    setAddress('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const setLocationAccess = async () => {
      const isLocationAccessible = await isLocationAccessPermitted();
      setLocationAccessEnabled(isLocationAccessible);
    };

    setLocationAccess();
  }, []);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [predictions.length]);

  const allPredictions = [currentLocationSuggestion, ...predictions];

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isInputFocused || !isDropdownOpen) {
      setIsInputFocused(true);
      setIsDropdownOpen(true);
    }

    if (!isDropdownOpen) {
      return;
    }

    const suggestionsLength = allPredictions.length;

    const keyActions: Record<string, () => void> = {
      ArrowDown: () => {
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1 >= suggestionsLength ? -1 : prev + 1));
      },
      ArrowUp: () => {
        e.preventDefault();
        setHighlightedIndex(prev => (prev - 1 < -1 ? suggestionsLength - 1 : prev - 1));
      },
      Enter: () => {
        if (highlightedIndex >= 0 && highlightedIndex < suggestionsLength) {
          e.preventDefault();
          const selectedSuggestion = allPredictions[highlightedIndex];
          if (selectedSuggestion) {
            handleSelect(selectedSuggestion.text.text, selectedSuggestion.placeId);
          }
        }
      },
      Escape: () => {
        e.preventDefault();
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      },
    };

    const action = keyActions[e.key];
    if (action) {
      action();
    }
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  return {
    inputRef,
    address,
    isInputFocused,
    locationAccessEnabled,
    currentAddressKey,
    isLoaded: isGoogleMapReady,
    predictions: allPredictions,
    highlightedIndex,
    onChange,
    handleSelect,
    onInputClick,
    onInputFocus: () => {
      setIsInputFocused(true);
      setIsDropdownOpen(true);
      if (removeValidationError) {
        removeValidationError();
      }
    },
    onInputBlur: () => setIsInputFocused(false),
    onInputKeyDown,
    clearInput,
    isDropdownOpen,
    closeDropdown,
  };
};

export default useAddressAutocomplete;
